import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { uniformFilterObjectToFlat } from '../utils/urlSync';

import type { UniformFilterObject } from '../utils/types';

export const FIRST_PAGE = 1;

/** Number of pages for `itemCount` items, never below one. */
export function getPageCount(itemCount: number, pageSize: number): number {
	if (!Number.isFinite(pageSize) || pageSize < 1) return FIRST_PAGE;
	return Math.max(FIRST_PAGE, Math.ceil(Math.max(0, itemCount) / pageSize));
}

/** Coerces any page value into `[1, pageCount]`. Invalid input reads as page one. */
export function clampPage(page: number | undefined, pageCount: number): number {
	if (page === undefined || !Number.isFinite(page)) return FIRST_PAGE;
	const integerPage = Math.floor(page);
	if (integerPage < FIRST_PAGE) return FIRST_PAGE;
	return Math.min(integerPage, Math.max(FIRST_PAGE, pageCount));
}

/** The items on a 1-based page. */
export function slicePage<T>(items: readonly T[], page: number, pageSize: number): T[] {
	const start = (page - FIRST_PAGE) * pageSize;
	return items.slice(start, start + pageSize);
}

interface PageStateOptions<T> {
	readonly items: readonly T[];
	readonly pageSize: number;
	/**
	 * Changes when the visitor changes a filter, the search query, or the sort.
	 * A change sends the visitor back to page one. Item arrivals (connector
	 * hydration) must not be part of the key, so a page survives them.
	 */
	readonly resetKey: string;
	/**
	 * The page the URL asks for. Applied on mount and on browser back/forward.
	 * Applied after the reset for the same render, so a shared link to page 3
	 * with filters lands on page 3.
	 */
	readonly externalPage?: number;
	/**
	 * Total item count of the resource while the browser still fetches pages.
	 * Keeps the page count stable during hydration.
	 */
	readonly totalHint?: number;
}

export interface PageState<T> {
	readonly currentPage: number;
	readonly pageCount: number;
	readonly pageNumbers: number[];
	readonly pageItems: T[];
	readonly setPage: (page: number) => void;
}

/**
 * Owns the 1-based page index for the paginated gallery mode.
 *
 * The requested page is state; the current page is derived, clamped to the
 * page count, so a filter that shrinks the list never leaves the visitor on an
 * empty page.
 */
export function usePageState<T>({
	items,
	pageSize,
	resetKey,
	externalPage,
	totalHint,
}: PageStateOptions<T>): PageState<T> {
	const [requestedPage, setRequestedPage] = useState<number>(() =>
		clampPage(externalPage, Number.POSITIVE_INFINITY),
	);

	const itemCount = Math.max(items.length, totalHint ?? 0);
	const pageCount = getPageCount(itemCount, pageSize);
	const currentPage = clampPage(requestedPage, pageCount);

	// Skip the mount: the initial state already honours `externalPage`.
	const previousResetKeyRef = useRef(resetKey);
	useEffect(() => {
		if (previousResetKeyRef.current === resetKey) return;
		previousResetKeyRef.current = resetKey;
		setRequestedPage(FIRST_PAGE);
	}, [resetKey]);

	// Declared after the reset effect on purpose: both fire in the same commit
	// when a URL hydrates filters and page together, and the last write wins.
	useEffect(() => {
		if (externalPage === undefined) return;
		setRequestedPage(clampPage(externalPage, Number.POSITIVE_INFINITY));
	}, [externalPage]);

	const setPage = useCallback((page: number) => {
		setRequestedPage(clampPage(page, Number.POSITIVE_INFINITY));
	}, []);

	const pageNumbers = useMemo(
		() => Array.from({ length: pageCount }, (_, index) => index + FIRST_PAGE),
		[pageCount],
	);

	const pageItems = useMemo(
		() => slicePage(items, currentPage, pageSize),
		[items, currentPage, pageSize],
	);

	return { currentPage, pageCount, pageNumbers, pageItems, setPage };
}

interface GalleryPaginationOptions<T> {
	readonly isPaginated: boolean;
	readonly items: readonly T[];
	/** Every loaded item, before filters. Tells a narrowed list from the full set. */
	readonly loadedItemCount: number;
	readonly pageSize: number | undefined;
	readonly filterObject: UniformFilterObject;
	readonly searchQuery: string;
	readonly sortId: string | undefined;
	readonly urlPage: number;
	readonly hasUnfetchedItems: boolean;
	readonly resourceTotal: number | undefined;
}

/**
 * Wires `usePageState` to the gallery's filter, search, sort, URL, and connector
 * state. A filter, search, or sort change sends the visitor to page one. Item
 * arrivals do not, so a connector page that lands keeps the current page. While
 * the browser still fetches pages, and no filter has narrowed the set, the
 * resource total keeps the page count stable.
 */
export function useGalleryPagination<T>({
	isPaginated,
	items,
	loadedItemCount,
	pageSize,
	filterObject,
	searchQuery,
	sortId,
	urlPage,
	hasUnfetchedItems,
	resourceTotal,
}: GalleryPaginationOptions<T>): PageState<T> {
	const resetKey = useMemo(
		() => JSON.stringify([uniformFilterObjectToFlat(filterObject), searchQuery, sortId]),
		[filterObject, searchQuery, sortId],
	);
	const isNarrowed = items.length !== loadedItemCount;
	const totalHint = getTotalHint(isPaginated, hasUnfetchedItems, isNarrowed, resourceTotal);

	return usePageState({
		items,
		pageSize: pageSize ?? Number.POSITIVE_INFINITY,
		resetKey,
		externalPage: isPaginated ? urlPage : undefined,
		totalHint,
	});
}

function getTotalHint(
	isPaginated: boolean,
	hasUnfetchedItems: boolean,
	isNarrowed: boolean,
	resourceTotal: number | undefined,
): number | undefined {
	if (!isPaginated || !hasUnfetchedItems || isNarrowed) return undefined;
	return resourceTotal;
}
